/**
 * Global state — auth + current screen.
 * Lightweight store (zustand) — no API logic, only UI state.
 */
import { create } from 'zustand';
import type { User, Community } from '@/lib/api';
import { authApi } from '@/lib/api';

export type Screen =
  | 'boot'
  | 'auth'
  | 'home'
  | 'door'
  | 'doorbell'
  | 'doorbell-share'
  | 'voicemails'
  | 'announcements'
  | 'incidents'
  | 'votes'
  | 'fees'
  | 'profile'
  | 'settings'
  | 'admin'
  | 'detail'
  | 'modal'
  | 'chat';

export interface DetailItem {
  id: string;
  type: 'announcement' | 'incident' | 'vote' | 'fee';
}

interface AppState {
  // Auth
  isAuthenticated: boolean;
  user: User | null;
  community: Community | null;
  // UI
  screen: Screen;
  prevScreen: Screen | null;
  menuIndex: number;
  toast: { id: string; text: string; kind: 'success' | 'error' | 'info' } | null;
  // Detail
  detail: DetailItem | null;
  // Modal
  modal: { kind: 'create-incident' | 'create-announcement' } | null;
  // Chat
  ringId: string | null;
  // Actions
  setAuth: (user: User, community: Community | null) => void;
  hydrateAuth: (next: { isAuthenticated: boolean; user: User | null; community: Community | null }) => void;
  logout: () => void;
  goto: (screen: Screen) => void;
  back: () => void;
  setMenuIndex: (n: number) => void;
  showToast: (text: string, kind?: 'success' | 'error' | 'info') => void;
  hideToast: () => void;
  openDetail: (item: DetailItem) => void;
  closeDetail: () => void;
  openModal: (kind: 'create-incident' | 'create-announcement') => void;
  closeModal: () => void;
  openChat: (ringId: string) => void;
  closeChat: () => void;
}

export const useApp = create<AppState>((set, get) => ({
  isAuthenticated: authApi.isAuthenticated(),
  user: authApi.getUser(),
  community: authApi.getCommunity(),
  screen: 'boot',
  prevScreen: null,
  menuIndex: 0,
  toast: null,
  detail: null,
  modal: null,
  ringId: null,

  setAuth: (user, community) => set({ isAuthenticated: true, user, community }),
  hydrateAuth: (next) =>
    set((s) => ({
      // Only update user/community if the bootstrap result actually has them —
      // preserves any in-memory changes from earlier in this session.
      isAuthenticated: next.isAuthenticated,
      user: next.user ?? s.user,
      community: next.community ?? s.community,
    })),
  logout: () => {
    authApi.logout();
    set({ isAuthenticated: false, user: null, community: null, screen: 'boot', prevScreen: null, detail: null, modal: null, ringId: null });
  },
  goto: (screen) => set((s) => ({ screen, prevScreen: s.screen, menuIndex: 0 })),
  back: () => {
    const prev = get().prevScreen;
    if (prev) set({ screen: prev, prevScreen: null });
    else set({ screen: 'home', prevScreen: null });
  },
  openChat: (ringId) => set((s) => ({ screen: 'chat', prevScreen: s.screen, ringId, menuIndex: 0 })),
  closeChat: () => {
    const prev = get().prevScreen;
    set({ ringId: null, screen: prev ?? 'home', prevScreen: null });
  },
  setMenuIndex: (n) => set({ menuIndex: n }),
  showToast: (text, kind = 'info') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set({ toast: { id, text, kind } });
  },
  hideToast: () => set({ toast: null }),
  openDetail: (item) => set({ detail: item, prevScreen: get().screen, screen: 'detail' }),
  closeDetail: () => {
    const prev = get().prevScreen;
    set({ detail: null, screen: prev ?? 'home', prevScreen: null });
  },
  openModal: (kind) => set({ modal: { kind }, prevScreen: get().screen, screen: 'modal' }),
  closeModal: () => {
    const prev = get().prevScreen;
    set({ modal: null, screen: prev ?? 'home', prevScreen: null });
  },
}));

// ============================================================================
// Door state machine — the heart of the experience
// ============================================================================

export type DoorState = 'idle' | 'opening' | 'success' | 'error' | 'not_configured';

interface DoorMachineState {
  state: DoorState;
  error: string | null;
  lastOpenedAt: number | null;
  setState: (state: DoorState, error?: string | null) => void;
  reset: () => void;
}

export const useDoorMachine = create<DoorMachineState>((set) => ({
  state: 'idle',
  error: null,
  lastOpenedAt: null,
  setState: (state, error = null) =>
    set({ state, error, lastOpenedAt: state === 'success' ? Date.now() : null }),
  reset: () => set({ state: 'idle', error: null }),
}));
